import { MediaFile } from '../../formats/video-sdk3/lib/vast/media-file';

const excludedMimeType = [
  'video/3gpp',
  'video/ogg',
  'video/x-flv',
  'video/quicktime'
];

const BYTE = 8;
const TOLERANCE_FOR_PORTRAIT = 0.2;
const TOLERANCE_FOR_LANDSCAPE = 0.1;
const MAX_WIDTH_FOR_PORTRAIT = 900;
const MAX_WIDTH_FOR_LANDSCAPE = 1280;
/*
 ** We have some discrepencies between the video size expected (this function) with the real video size.
 ** This is why we add some if statements.
 ** We found a pattern with videos which have a width greater than 256 that have in reality 200 additional bitrates.
 ** 256 and less have -100 bitrates.
 */
export function getVideoMbSize(mediaFile: MediaFile): number {
  const { bitrate, duration, additionalBitrate = 0 } = mediaFile || {};
  return ((Number(bitrate) + additionalBitrate) * Number(duration)) / BYTE;
}

export function isExcludedType(mediaFile: MediaFile): boolean {
  const type = mediaFile.type || '';
  return excludedMimeType.indexOf(type.toLowerCase()) > -1;
}

export function getClosestResolution(
  mediaFiles: MediaFile[],
  targetResolution: { width: number; height: number }
): MediaFile {
  const mediaFilesSorted = [...mediaFiles];
  mediaFilesSorted.sort(
    (a, b) =>
      Math.abs(targetResolution.width - a.width) -
      Math.abs(targetResolution.width - b.width)
  );
  return mediaFilesSorted[0];
}

export function excludeVideoTooLarge(
  mediaFiles: MediaFile[],
  limitSize: number
): MediaFile[] {
  return mediaFiles.filter(mediaFile => getVideoMbSize(mediaFile) < limitSize);
}

export function filterByTargetRatio(
  mediaFiles: MediaFile[],
  targetRatio: number
): MediaFile[] {
  const mediaFilesFiltered = [...mediaFiles];

  let closestRatio =
    mediaFilesFiltered[0].width / (mediaFilesFiltered[0].height || 1);

  mediaFilesFiltered.forEach(mediaFile => {
    const ratioVideo = mediaFile.width / (mediaFile.height || 1);

    if (
      Math.abs(targetRatio - ratioVideo) < Math.abs(targetRatio - closestRatio)
    ) {
      closestRatio = ratioVideo;
    }
  });

  // We filter with a 0.05 gap to increase the allowed range of compliant ratio
  return mediaFilesFiltered.filter(
    mediaFile =>
      Math.abs(mediaFile.width / (mediaFile.height || 1) - closestRatio) <= 0.05
  );
}

export function sortBySize(
  mediaFiles: MediaFile[],
  resolutionSorted: string = 'asc'
): MediaFile[] {
  const mediaFilesFiltered = [...mediaFiles];

  return mediaFilesFiltered.sort((a, b) => {
    if (resolutionSorted === 'desc') {
      return b.width * b.height - a.width * a.height;
    } else {
      return a.width * a.height - b.width * b.height;
    }
  });
}

export function groupByMimeType(mediaFiles: MediaFile[]): MediaFile[][] {
  const groupedVideos = mediaFiles.reduce(
    (res: Record<string, MediaFile[]>, mediaFile) => {
      res[mediaFile.type] = [...(res[mediaFile.type] || []), mediaFile];
      return res;
    },
    {}
  );

  return Object.keys(groupedVideos).map(type => groupedVideos[type]);
}

export function sortBySdkPreferences(mediaFiles: MediaFile[]): MediaFile[] {
  let mediaFileSorted = [...mediaFiles];

  // extract hls to put at the end of the array
  const extractHls: MediaFile[] = [];
  mediaFileSorted.map((a, i) => {
    if (a.type === 'application/x-mpegURL') {
      extractHls.push(a);
      delete mediaFileSorted[i];
    }
  });

  // clean undefined values
  mediaFileSorted = mediaFileSorted.filter(element => {
    return element !== undefined;
  }) as MediaFile[];

  // sort mp4 first
  mediaFileSorted.sort((a, b) => {
    if (a.type === b.type) {
      return 0;
    } else if (b.type === 'video/mp4') {
      return 1;
    } else {
      return -1;
    }
  });

  mediaFileSorted.push(...extractHls);
  return mediaFileSorted;
}

export function pickByWidth(
  sortedAscByWidth: MediaFile[],
  targetWidth: number,
  orientation: 'portrait' | 'landscape' | 'square'
): MediaFile {
  // Allow more undershoot in portrait to avoid jumping to 720p
  const tol =
    orientation === 'portrait'
      ? TOLERANCE_FOR_PORTRAIT
      : TOLERANCE_FOR_LANDSCAPE; // 20% portrait, 10% landscape
  const minAcceptable = Math.floor(targetWidth * (1 - tol));

  const candidates = sortedAscByWidth.filter(m => m.width >= minAcceptable);
  if (candidates.length > 0) {
    // Among closest widths, prefer lower bitrate to reduce stalls
    const head = candidates[0];
    const near = candidates.filter(
      m =>
        Math.abs(m.width - head.width) <=
        Math.max(1, Math.round(head.width * 0.1))
    );
    return near.sort((a, b) => {
      const ab = a.bitrate;
      const bb = b.bitrate;

      if (ab != null && bb != null) {
        // Both have bitrate → prefer lower bitrate
        return ab - bb;
      }

      if (ab == null && bb == null) {
        // Neither has bitrate → keep width order
        return (
          Math.abs(a.width - targetWidth) - Math.abs(b.width - targetWidth)
        );
      }

      if (ab == null) {
        // a has no bitrate → fallback to width comparison
        return (
          Math.abs(a.width - targetWidth) - Math.abs(b.width - targetWidth)
        );
      }

      if (bb == null) {
        // b has no bitrate → fallback to width comparison
        return (
          Math.abs(a.width - targetWidth) - Math.abs(b.width - targetWidth)
        );
      }

      return 0;
    })[0];
  }
  // Fallback: largest below target
  return sortedAscByWidth[sortedAscByWidth.length - 1];
}

export function getOrientationFromSize(t: {
  width: number;
  height: number;
}): 'landscape' | 'portrait' | 'square' {
  const r = t.width / Math.max(1, t.height);
  if (r > 1.15) return 'landscape';
  if (r < 0.85) return 'portrait';
  return 'square';
}

export function normalizeTarget(target: {
  width: number;
  height: number;
}): {
  width: number;
  height: number;
  orientation: 'landscape' | 'portrait' | 'square';
} {
  // Apply DPR then clamp by orientation to avoid over-selecting large renditions
  const DPR = 1.5;
  const w = Math.round(target.width * DPR);
  const h = Math.round(target.height * DPR);

  const orientation = getOrientationFromSize({ width: w, height: h });
  const maxW =
    orientation === 'portrait'
      ? MAX_WIDTH_FOR_PORTRAIT
      : MAX_WIDTH_FOR_LANDSCAPE; // clamp portrait lower to favor 480p
  const normW = Math.min(w, maxW);
  const normH = Math.round(normW * (h / Math.max(1, w))); // preserve aspect

  return { width: normW, height: normH, orientation };
}
